"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import type { KeyboardEvent } from "react";
import { useEffect, useRef, useState, useId } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useClickAway, useDebounce, useKeyPressEvent } from "react-use";
import type { CommunityMemberMinimal } from "@/entities/IProfile";
import { commonApiFetch } from "@/services/api/common-api";
import CommonProfileSearchItems from "../profile-search/CommonProfileSearchItems";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getSelectableIdentity, getSelectableIdentityOption, type SelectableIdentityOption } from "@/components/utils/input/profile-search/getSelectableIdentity";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
export enum IdentitySearchSize {
  SM = "SM",
  MD = "MD",
}
const MIN_SEARCH_LENGTH = 3;
type IdentitySearchDraft = {
  readonly value: string;
  readonly baseResolvedDisplayValue: string | null;
  readonly preservedResolvedValues: readonly (string | null)[];
};
export default function IdentitySearch(t0) {
  const $ = _c(97);
  const {
    identity,
    size: t1,
    label: t2,
    error: t3,
    errorMessage,
    autoFocus: t4,
    selectedDisplayValue,
    clearable: t5,
    dropdownListClassName,
    onSelectionChange,
    setIdentity
  } = t0;
  const size = t1 === undefined ? IdentitySearchSize.MD : t1;
  const label = t2 === undefined ? "Identity" : t2;
  const error = t3 === undefined ? false : t3;
  const autoFocus = t4 === undefined ? false : t4;
  const clearable = t5 === undefined ? true : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      [IdentitySearchSize.SM]: "tw-py-3 tw-text-sm",
      [IdentitySearchSize.MD]: "tw-pb-3 tw-pt-3 tw-text-md"
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const INPUT_CLASSES = t6;
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      [IdentitySearchSize.SM]: "tw-text-sm",
      [IdentitySearchSize.MD]: "tw-text-md"
    };
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const LABEL_CLASSES = t7;
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      [IdentitySearchSize.SM]: "tw-top-3",
      [IdentitySearchSize.MD]: "tw-top-3.5"
    };
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const ICON_CLASSES = t8;
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const resolvedDisplayValue = selectedDisplayValue ?? identity ?? null;
  const [searchCriteriaDraft, setSearchCriteriaDraft] = useState(null);
  const shouldUseDraft = searchCriteriaDraft !== null && (searchCriteriaDraft.baseResolvedDisplayValue === resolvedDisplayValue || searchCriteriaDraft.preservedResolvedValues.some(value => value === resolvedDisplayValue));
  const searchCriteria = shouldUseDraft ? searchCriteriaDraft.value : resolvedDisplayValue;
  const [debouncedValue, setDebouncedValue] = useState(searchCriteria);
  let t9;
  if ($[3] !== searchCriteria) {
    t9 = () => setDebouncedValue(searchCriteria);
    $[3] = searchCriteria;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== searchCriteria) {
    t10 = [searchCriteria];
    $[5] = searchCriteria;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  useDebounce(t9, 200, t10);
  let t11;
  let t12;
  if ($[7] !== debouncedValue) {
    t11 = [QueryKey.PROFILE_SEARCH, {
      param: debouncedValue,
      only_profile_owners: "true"
    }];
    t12 = async () => await commonApiFetch({
      endpoint: "community-members",
      params: {
        param: debouncedValue ?? "",
        only_profile_owners: "true"
      }
    });
    $[7] = debouncedValue;
    $[8] = t11;
    $[9] = t12;
  } else {
    t11 = $[8];
    t12 = $[9];
  }
  const t13 = !!debouncedValue && debouncedValue.length >= MIN_SEARCH_LENGTH;
  let t14;
  if ($[10] !== t11 || $[11] !== t12 || $[12] !== t13) {
    t14 = {
      queryKey: t11,
      queryFn: t12,
      enabled: t13
    };
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  const {
    data
  } = useQuery(t14);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [highlightedOptionId, setHighlightedOptionId] = useState(undefined);
  const [shouldSubmit, setShouldSubmit] = useState(false);
  let t15;
  if ($[14] !== onSelectionChange || $[15] !== resolvedDisplayValue || $[16] !== setIdentity) {
    t15 = (newValue, options) => {
      const draftValue = options?.displayValue ?? newValue ?? "";
      setIdentity(newValue);
      onSelectionChange?.(options?.selection ?? null);
      setSearchCriteriaDraft({
        value: draftValue,
        baseResolvedDisplayValue: resolvedDisplayValue,
        preservedResolvedValues: [newValue, options?.displayValue ?? null]
      });
      setIsOpen(false);
      setHighlightedIndex(null);
    };
    $[14] = onSelectionChange;
    $[15] = resolvedDisplayValue;
    $[16] = setIdentity;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  const onValueChange = t15;
  let t16;
  if ($[18] !== searchCriteria?.length) {
    t16 = newV => {
      if (newV) {
        const len = searchCriteria?.length ?? 0;
        setIsOpen(len >= MIN_SEARCH_LENGTH);
        return;
      }
      setIsOpen(false);
      setHighlightedIndex(null);
    };
    $[18] = searchCriteria?.length;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const onFocusChange = t16;
  let t17;
  if ($[20] !== clearable || $[21] !== onSelectionChange || $[22] !== resolvedDisplayValue || $[23] !== setIdentity) {
    t17 = newV_0 => {
      setSearchCriteriaDraft({
        value: newV_0 ?? "",
        baseResolvedDisplayValue: resolvedDisplayValue,
        preservedResolvedValues: []
      });
      const len_0 = newV_0?.length ?? 0;
      setIsOpen(len_0 >= MIN_SEARCH_LENGTH);
      if (!newV_0 && clearable) {
        setIdentity(null);
        onSelectionChange?.(null);
      }
      setHighlightedIndex(null);
    };
    $[20] = clearable;
    $[21] = onSelectionChange;
    $[22] = resolvedDisplayValue;
    $[23] = setIdentity;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const onSearchCriteriaChange = t17;
  let t18;
  if ($[25] !== onValueChange) {
    t18 = profile => {
      const nextSelection = getSelectableIdentityOption(profile);
      if (!nextSelection) {
        return false;
      }
      onValueChange(nextSelection.value, {
        displayValue: nextSelection.label,
        selection: nextSelection
      });
      return true;
    };
    $[25] = onValueChange;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const selectProfile = t18;
  const wrapperRef = useRef(null);
  let t19;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setIsOpen(false);
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  useClickAway(wrapperRef, t19);
  let t20;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setIsOpen(false);
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  useKeyPressEvent("Escape", t20);
  const inputRef = useRef(null);
  const shouldAutoFocus = useRef(autoFocus);
  let t21;
  let t22;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => {
      if (shouldAutoFocus.current) {
        inputRef.current?.focus();
      }
    };
    t22 = [];
    $[29] = t21;
    $[30] = t22;
  } else {
    t21 = $[29];
    t22 = $[30];
  }
  useEffect(t21, t22);
  let t23;
  let t24;
  if ($[31] !== shouldSubmit) {
    t23 = () => {
      if (!shouldSubmit) {
        return;
      }
      const formElement = inputRef.current?.form;
      if (formElement) {
        formElement.requestSubmit();
      }
      setShouldSubmit(false);
    };
    t24 = [shouldSubmit];
    $[31] = shouldSubmit;
    $[32] = t23;
    $[33] = t24;
  } else {
    t23 = $[32];
    t24 = $[33];
  }
  useEffect(t23, t24);
  let t25;
  if ($[34] !== data || $[35] !== highlightedIndex || $[36] !== selectProfile) {
    t25 = event => {
      if (!data?.length) {
        return;
      }
      const maxIndex = data.length - 1;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(current => {
          if (current === null || current >= maxIndex) {
            return 0;
          }
          return current + 1;
        });
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(current_0 => {
          if (current_0 === null || current_0 <= 0) {
            return maxIndex;
          }
          return current_0 - 1;
        });
        return;
      }
      if (event.key === "Enter" && highlightedIndex !== null) {
        event.preventDefault();
        const profile_0 = data[highlightedIndex];
        if (profile_0) {
          if (selectProfile(profile_0)) {
            setShouldSubmit(true);
          }
        }
      }
    };
    $[34] = data;
    $[35] = highlightedIndex;
    $[36] = selectProfile;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const handleArrowNavigation = t25;
  let t26;
  let t27;
  if ($[38] !== isOpen) {
    t26 = () => {
      if (!isOpen) {
        setHighlightedIndex(null);
      }
    };
    t27 = [isOpen];
    $[38] = isOpen;
    $[39] = t26;
    $[40] = t27;
  } else {
    t26 = $[39];
    t27 = $[40];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[41] !== data || $[42] !== identity) {
    t28 = () => {
      if (!data?.length) {
        setHighlightedIndex(null);
        return;
      }
      if (identity) {
        const matchingIndex = data.findIndex(profile_1 => {
          const value_0 = getSelectableIdentity(profile_1);
          return value_0?.toLowerCase() === identity.toLowerCase();
        });
        if (matchingIndex >= 0) {
          setHighlightedIndex(matchingIndex);
          return;
        }
      }
      setHighlightedIndex(current_1 => current_1 === null ? null : Math.min(current_1, data.length - 1));
    };
    t29 = [data, identity];
    $[41] = data;
    $[42] = identity;
    $[43] = t28;
    $[44] = t29;
  } else {
    t28 = $[43];
    t29 = $[44];
  }
  useEffect(t28, t29);
  const hasIdentity = identity !== null && identity.length > 0;
  const t30 = searchCriteria ?? "";
  let t31;
  if ($[45] !== onSearchCriteriaChange) {
    t31 = e => onSearchCriteriaChange(e.target.value);
    $[45] = onSearchCriteriaChange;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  let t33;
  if ($[47] !== onFocusChange) {
    t32 = () => onFocusChange(true);
    t33 = e_0 => {
      const next = e_0.relatedTarget as Node | null;
      if (!next || !wrapperRef.current?.contains(next)) {
        onFocusChange(false);
      }
    };
    $[47] = onFocusChange;
    $[48] = t32;
    $[49] = t33;
  } else {
    t32 = $[48];
    t33 = $[49];
  }
  let t34;
  if ($[50] !== handleArrowNavigation) {
    t34 = event_0 => handleArrowNavigation(event_0);
    $[50] = handleArrowNavigation;
    $[51] = t34;
  } else {
    t34 = $[51];
  }
  const t35 = isOpen && highlightedOptionId ? highlightedOptionId : undefined;
  const t36 = `${INPUT_CLASSES[size]} ${error ? "tw-caret-error tw-ring-error focus:tw-border-error focus:tw-ring-error" : "tw-caret-primary-400 tw-ring-iron-700 hover:tw-ring-iron-650 focus:tw-border-blue-500 focus:tw-ring-primary-400"} tw-peer tw-form-input tw-block tw-w-full tw-appearance-none tw-rounded-lg tw-border-0 tw-border-iron-700 tw-bg-iron-900 tw-pl-10 tw-pr-4 tw-text-base tw-font-medium tw-shadow-sm tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out placeholder:tw-text-iron-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset ${searchCriteria ? "tw-text-primary-400 focus:tw-text-white" : "tw-text-white"}`;
  let t37;
  if ($[52] !== inputId || $[53] !== isOpen || $[54] !== listboxId || $[55] !== t30 || $[56] !== t31 || $[57] !== t32 || $[58] !== t33 || $[59] !== t34 || $[60] !== t35 || $[61] !== t36) {
    t37 = <input ref={inputRef} type="text" value={t30} onChange={t31} onFocus={t32} onBlur={t33} onKeyDown={t34} id={inputId} autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={isOpen} aria-controls={listboxId} aria-activedescendant={t35} className={t36} placeholder=" " />;
    $[52] = inputId;
    $[53] = isOpen;
    $[54] = listboxId;
    $[55] = t30;
    $[56] = t31;
    $[57] = t32;
    $[58] = t33;
    $[59] = t34;
    $[60] = t35;
    $[61] = t36;
    $[62] = t37;
  } else {
    t37 = $[62];
  }
  const t38 = `${ICON_CLASSES[size]} tw-pointer-events-none tw-absolute tw-left-3 tw-h-5 tw-w-5 tw-text-iron-300`;
  let t39;
  if ($[63] !== t38) {
    t39 = <MagnifyingGlassIcon className={t38} aria-hidden="true" />;
    $[63] = t38;
    $[64] = t39;
  } else {
    t39 = $[64];
  }
  let t40;
  if ($[65] !== clearable || $[66] !== hasIdentity || $[67] !== onValueChange || $[68] !== size) {
    t40 = clearable && hasIdentity && <button type="button" aria-label="Clear identity" onClick={() => onValueChange(null)} className={`${ICON_CLASSES[size]} tw-absolute tw-right-3 tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-iron-400 tw-transition tw-duration-300 tw-ease-out hover:tw-text-error focus:tw-outline-none focus:tw-ring-0`}><FontAwesomeIcon icon={faXmark} className="tw-h-5 tw-w-5" /></button>;
    $[65] = clearable;
    $[66] = hasIdentity;
    $[67] = onValueChange;
    $[68] = size;
    $[69] = t40;
  } else {
    t40 = $[69];
  }
  const t41 = `${LABEL_CLASSES[size]} ${error ? "peer-placeholder-shown:tw-top-1/4 peer-placeholder-shown:-tw-translate-y-1/4 peer-focus:tw-text-error" : "peer-placeholder-shown:tw-top-1/2 peer-placeholder-shown:-tw-translate-y-1/2 peer-focus:tw-text-primary-400"} tw-absolute tw-start-1 tw-top-2 tw-z-10 tw-ml-7 tw-origin-[0] -tw-translate-y-4 tw-scale-75 tw-transform tw-cursor-text tw-rounded-lg tw-bg-iron-900 tw-px-2 tw-font-medium tw-text-iron-500 tw-duration-300 peer-placeholder-shown:tw-top-1/2 peer-placeholder-shown:-tw-translate-y-1/2 peer-placeholder-shown:tw-scale-100 peer-focus:tw-top-2 peer-focus:-tw-translate-y-4 peer-focus:tw-scale-75 peer-focus:tw-bg-iron-900 peer-focus:tw-px-2 peer-focus:tw-text-primary-400 rtl:peer-focus:tw-left-auto rtl:peer-focus:tw-translate-x-1/4`;
  let t42;
  if ($[70] !== inputId || $[71] !== label || $[72] !== t41) {
    t42 = <label htmlFor={inputId} className={t41}>{label}</label>;
    $[70] = inputId;
    $[71] = label;
    $[72] = t41;
    $[73] = t42;
  } else {
    t42 = $[73];
  }
  let t43;
  if ($[74] !== data) {
    t43 = data ?? [];
    $[74] = data;
    $[75] = t43;
  } else {
    t43 = $[75];
  }
  let t44;
  if ($[76] !== selectProfile) {
    t44 = profile_2 => {
      if (!profile_2) {
        return;
      }
      selectProfile(profile_2);
    };
    $[76] = selectProfile;
    $[77] = t44;
  } else {
    t44 = $[77];
  }
  let t45;
  if ($[78] !== dropdownListClassName || $[79] !== highlightedIndex || $[80] !== identity || $[81] !== isOpen || $[82] !== listboxId || $[83] !== searchCriteria || $[84] !== t43 || $[85] !== t44) {
    t45 = <CommonProfileSearchItems open={isOpen} selected={identity} searchCriteria={searchCriteria} profiles={t43} highlightedIndex={highlightedIndex} listboxId={listboxId} listClassName={dropdownListClassName} onHighlightedOptionIdChange={setHighlightedOptionId} onProfileSelect={t44} />;
    $[78] = dropdownListClassName;
    $[79] = highlightedIndex;
    $[80] = identity;
    $[81] = isOpen;
    $[82] = listboxId;
    $[83] = searchCriteria;
    $[84] = t43;
    $[85] = t44;
    $[86] = t45;
  } else {
    t45 = $[86];
  }
  let t46;
  if ($[87] !== error || $[88] !== errorMessage) {
    t46 = error && <div className="tw-relative tw-flex tw-items-center tw-gap-x-2 tw-pt-1.5"><FontAwesomeIcon icon={faCircleExclamation} className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-error" aria-hidden="true" /><div className="tw-text-xs tw-font-medium tw-text-error">{errorMessage ?? "Please enter identity"}</div></div>;
    $[87] = error;
    $[88] = errorMessage;
    $[89] = t46;
  } else {
    t46 = $[89];
  }
  let t47;
  if ($[90] !== t37 || $[91] !== t39 || $[92] !== t40 || $[93] !== t42 || $[94] !== t45 || $[95] !== t46) {
    t47 = <div className="tw-group tw-relative tw-w-full" ref={wrapperRef}>{t37}{t39}{t40}{t42}{t45}{t46}</div>;
    $[90] = t37;
    $[91] = t39;
    $[92] = t40;
    $[93] = t42;
    $[94] = t45;
    $[95] = t46;
    $[96] = t47;
  } else {
    t47 = $[96];
  }
  return t47;
}